import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './ResultsPage.css';
import { getIssues } from './Page3';
import {
  API_URL, STATE_NAMES, OFFICE_NAMES, ResultsTable, summaryFromRows, raceTitleFromRow, pollsClosed, fmtUpdated, partyColor,
} from './ResultsTable';

// ── Election Results page ──
//   /results                 search + every state
//   /results/IL              every race in Illinois (filter box)
//   /results/IL/IL-governor  one race (its own page, title and description — for Google)
//   /ballot                  what's on the ballot — every state
//   /ballot/IL               every race and candidate on the Illinois ballot (for "Illinois ballot 2026" searches)
// Each view sets its own <title>, description, canonical link and breadcrumb data so search engines can list it.

const SITE = 'https://vote.indtimes.news';
const STATE_BY_NAME = Object.fromEntries(Object.entries(STATE_NAMES).map(([k, v]) => [v.toLowerCase(), k]));

const SECTIONS = [
  ['U.S. Senate', t => t === 'senate', true],
  ['Governor & Statewide Offices', t => !['senate', 'house', 'measures', 'state-senate', 'state-house', 'governors-council', 'executive-council', 'oha-trustee', 'board-of-education', 'university-regents', 'public-service-commission', 'railroad-commission', 'mine-inspector', 'board-of-equalization'].includes(t), true],
  ['Ballot Measures', t => t === 'measures', true],
  ['U.S. House', t => t === 'house', true],
  ['Boards & Commissions', t => ['governors-council', 'executive-council', 'oha-trustee', 'board-of-education', 'university-regents', 'public-service-commission', 'railroad-commission', 'mine-inspector', 'board-of-equalization'].includes(t), true],
  ['State Senate', t => t === 'state-senate', false],
  ['State House', t => t === 'state-house', false],
];

function parsePath() {
  const parts = window.location.pathname.split('/').filter(Boolean).map(decodeURIComponent);
  const st = (parts[1] || '').toUpperCase();
  return { section: parts[0] === 'ballot' ? 'ballot' : parts[0] === 'guide' ? 'guide' : 'results', state: STATE_NAMES[st] ? st : '', raceId: STATE_NAMES[st] ? (parts[2] || '') : '', q: new URLSearchParams(window.location.search).get('q') || '' };
}

function setMeta({ title, description, path, crumbs }) {
  document.title = title;
  const set = (selector, make, attr, value) => {
    let el = document.head.querySelector(selector);
    if (!el) { el = make(); document.head.appendChild(el); }
    el.setAttribute(attr, value);
  };
  const meta = (key, isProp) => () => { const m = document.createElement('meta'); m.setAttribute(isProp ? 'property' : 'name', key); return m; };
  set('meta[name="description"]', meta('description'), 'content', description);
  set('meta[property="og:title"]', meta('og:title', true), 'content', title);
  set('meta[property="og:description"]', meta('og:description', true), 'content', description);
  set('meta[property="og:url"]', meta('og:url', true), 'content', SITE + path);
  set('meta[name="twitter:title"]', meta('twitter:title'), 'content', title);
  set('meta[name="twitter:description"]', meta('twitter:description'), 'content', description);
  set('link[rel="canonical"]', () => { const l = document.createElement('link'); l.setAttribute('rel', 'canonical'); return l; }, 'href', SITE + path);
  let ld = document.getElementById('ld-results');
  if (!ld) { ld = document.createElement('script'); ld.type = 'application/ld+json'; ld.id = 'ld-results'; document.head.appendChild(ld); }
  ld.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': [
      { '@type': 'WebPage', name: title, description, url: SITE + path, isPartOf: { '@type': 'WebSite', name: 'IndTimes Vote', url: SITE + '/' } },
      { '@type': 'BreadcrumbList', itemListElement: crumbs.map(([name, p], i) => ({ '@type': 'ListItem', position: i + 1, name, item: SITE + p })) },
    ],
  });
}

async function getJson(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}
// The state's results list (built by the indy-results Lambda, split into parts; only the newest build is used)
async function loadStateIndex(state) {
  const data = await getJson(`${API_URL}/candidates?raceId=results-${state}`);
  const rows = (data.candidates || []).filter(r => r.data);
  if (!rows.length) return [];
  const newest = rows.map(r => r.build || '').sort().pop();
  return rows.filter(r => (r.build || '') === newest)
    .sort((a, b) => String(a.candidateid).localeCompare(String(b.candidateid), 'en', { numeric: true }))
    .flatMap(r => { try { return JSON.parse(r.data); } catch { return []; } });
}

function Link({ href, go, className, children }) {
  return <a href={href} className={className} onClick={e => { if (e.metaKey || e.ctrlKey || e.shiftKey) return; e.preventDefault(); go(href); }}>{children}</a>;
}

// ── /results ──
function Landing({ go, initialQuery }) {
  const [q, setQ] = useState(initialQuery);
  const [busy, setBusy] = useState(false);
  const [hits, setHits] = useState(null);

  useEffect(() => {
    setMeta({
      title: '2026 Election Results — Live Results for Every Race on Your Ballot | IndTimes Vote',
      description: 'Live, unofficial November 3, 2026 general election results for U.S. Senate, U.S. House, Governor, statewide offices, ballot measures and State Legislature races in all 50 states. Search by candidate, state or race.',
      path: '/results', crumbs: [['Home', '/'], ['Election Results', '/results']],
    });
  }, []);

  const search = async e => {
    e?.preventDefault();
    const text = q.trim();
    if (text.length < 2) return;
    const lower = ` ${text.toLowerCase()} `;
    // A state in the question → go to that state's results, keeping the rest as a filter ("illinois governor")
    const name = Object.keys(STATE_BY_NAME).sort((a, b) => b.length - a.length).find(n => lower.includes(` ${n} `) || lower.includes(` ${n}?`));
    const abbr = !name && /^[a-z]{2}$/i.test(text) && STATE_NAMES[text.toUpperCase()] ? text.toUpperCase() : '';
    const state = name ? STATE_BY_NAME[name] : abbr;
    if (state) {
      const rest = name ? text.replace(new RegExp(name, 'i'), '').replace(/\b(results?|election|elections|2026|race|races|ballot)\b/gi, '').replace(/\s+/g, ' ').trim() : '';
      go(`/results/${state}${rest ? `?q=${encodeURIComponent(rest)}` : ''}`);
      return;
    }
    setBusy(true); setHits(null);
    try {
      const data = await getJson(`${API_URL}/search?q=${encodeURIComponent(text)}`);
      setHits((data.results || []).filter(r => r.raceId && r.state && r.racetype !== 'measures'));
    } catch { setHits([]); }
    setBusy(false);
  };

  return (
    <>
      <section className="rp-hero">
        <div className="rp-hero-inner">
          <p className="rp-eyebrow">November 3, 2026 General Election</p>
          <h1 className="rp-title">Election Results</h1>
          <p className="rp-lead">Live results for every race in our guide — U.S. Senate, U.S. House, Governor, statewide offices, ballot measures, boards and the State Legislature.</p>
          <form className="rp-search" onSubmit={search} role="search">
            <label htmlFor="rp-q" className="rp-sr">Search results by candidate, state or race</label>
            <input id="rp-q" value={q} onChange={e => setQ(e.target.value)} placeholder="Candidate, state or race" autoComplete="off" />
            <button type="submit" disabled={busy || q.trim().length < 2}>{busy ? 'Searching…' : 'Search'}</button>
          </form>
        </div>
      </section>
      <main className="rp-body">
        {hits && (
          <section className="rp-hits" aria-live="polite">
            {hits.length === 0 ? <p className="rp-muted">No candidate matched “{q}”. Try a full name, or a state and office such as “Texas senate”.</p> : (
              <>
                <h2 className="rp-h2">Candidates</h2>
                <ul className="rp-hit-list">
                  {hits.map(h => {
                    const href = `/results/${h.state}/${encodeURIComponent(h.raceId)}`;
                    return (
                      <li key={`${h.raceId}#${h.candidateid}`}>
                        <Link href={href} go={go} className="rp-hit">
                          <span className="rp-hit-name">{h.name}{h.resultWinner === 'true' ? ' ✓' : ''}</span>
                          <span className="rp-hit-race">{h.party ? `${h.party} · ` : ''}{raceTitleFromRow(h)}</span>
                          {h.resultPct ? <span className="rp-hit-pct">{Number(h.resultPct).toFixed(1)}%</span> : null}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </section>
        )}
        <section>
          <h2 className="rp-h2">Results by state</h2>
          <ul className="rp-states">
            {Object.entries(STATE_NAMES).map(([code, nm]) => (
              <li key={code}><Link href={`/results/${code}`} go={go} className="rp-state">{nm}</Link></li>
            ))}
          </ul>
        </section>
        <About />
      </main>
    </>
  );
}

// ── /results/IL ──
function StatePage({ state, go, initialFilter }) {
  const [races, setRaces] = useState(null);
  const [filter, setFilter] = useState(initialFilter);
  const stateName = STATE_NAMES[state];

  useEffect(() => {
    setMeta({
      title: `${stateName} Election Results 2026 — Governor, Senate, House & Ballot Measures | IndTimes Vote`,
      description: `Live, unofficial ${stateName} 2026 general election results: U.S. Senate, U.S. House, Governor, statewide offices, ballot measures and State Legislature races, with votes and percentages.`,
      path: `/results/${state}`, crumbs: [['Home', '/'], ['Election Results', '/results'], [stateName, `/results/${state}`]],
    });
    let off = false;
    setRaces(null);
    loadStateIndex(state).then(r => { if (!off) setRaces(r); }).catch(() => { if (!off) setRaces([]); });
    return () => { off = true; };
  }, [state, stateName]);

  const f = filter.trim().toLowerCase();
  const words = f.split(/\s+/).filter(Boolean);
  const shown = (races || []).filter(r => !words.length || words.every(w =>
    `${r.title} ${OFFICE_NAMES[r.t] || ''} ${(r.c || []).map(c => `${c.n} ${c.p}`).join(' ')} ${(r.m || []).map(m => m.n).join(' ')}`.toLowerCase().includes(w)));
  const newest = (races || []).map(r => r.upd || '').sort().pop();

  return (
    <>
      <section className="rp-hero">
        <div className="rp-hero-inner">
          <nav className="rp-crumbs" aria-label="Breadcrumb"><Link href="/results" go={go}>Election Results</Link> › <span>{stateName}</span></nav>
          <h1 className="rp-title">{stateName} Election Results</h1>
          <p className="rp-lead">November 3, 2026 general election{newest ? ` · last checked ${fmtUpdated(newest)}` : ''}</p>
          <div className="rp-search">
            <label htmlFor="rp-f" className="rp-sr">Filter {stateName} races</label>
            <input id="rp-f" value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filter: a name, office or district — e.g. “district 12”" autoComplete="off" />
          </div>
        </div>
      </section>
      <main className="rp-body">
        {races === null ? <div className="rp-loading"><span className="rp-spinner" /> Loading {stateName} results…</div>
          : races.length === 0 ? (
            <p className="rp-muted">{pollsClosed() ? `Results for ${stateName} are on their way — check back shortly.` : `${stateName} results will appear here after polls close on November 3.`} <a href="/">Find the candidates on your ballot →</a></p>
          ) : shown.length === 0 ? <p className="rp-muted">No {stateName} race matches “{filter}”.</p> : (
            SECTIONS.map(([label, test, open]) => {
              const list = shown.filter(r => test(r.t));
              if (!list.length) return null;
              return (
                <details key={label} className="rp-section" open={open || words.length > 0 || list.length <= 6}>
                  <summary><h2 className="rp-h2 rp-h2--inline">{label}</h2> <span className="rp-count">{list.length} race{list.length > 1 ? 's' : ''}</span></summary>
                  <div className="rp-grid">
                    {list.map(r => (
                      <article key={r.id} className="rp-card">
                        <ResultsTable race={r} showTitle titleHref={`/results/${state}/${encodeURIComponent(r.id)}`} headingLevel={3} />
                      </article>
                    ))}
                  </div>
                </details>
              );
            })
          )}
        <p className="rp-back-row"><Link href="/results" go={go}>← All states</Link></p>
        <About />
      </main>
    </>
  );
}

// ── /results/IL/IL-governor ──
// A real answer (not "No public statement found…" / "Position information not available.")
const realAnswer = t => typeof t === 'string' && t.trim().length > 3
  && !/^(no public statement|no information found|position information not available|not available|n\/a)/i.test(t.trim());

// One candidate's profile: role, bio, website and every position on file (this text is what people search for)
function CandidateProfile({ c, issues, open }) {
  const answers = issues.map(i => ({ i, text: c.positions?.[String(i.id)] })).filter(x => realAnswer(x.text));
  const src = id => c.positionSources?.[String(id)]?.url;
  return (
    <details className="rp-cand" open={open}>
      <summary>
        <span className="rt-dot" style={{ background: partyColor(c.party) }} />
        <h3 className="rp-cand-name">{c.name}</h3>
        {c.party && <span className="rp-cand-party">{c.party}</span>}
        <span className="rp-count">{answers.length ? `${answers.length} position${answers.length > 1 ? 's' : ''}` : ''}</span>
      </summary>
      {c.currentRole && <p className="rp-cand-role">{c.currentRole}</p>}
      {c.candidateBio && c.candidateBio !== c.currentRole && <p className="rp-cand-bio">{c.candidateBio}</p>}
      {c.website && <p className="rp-cand-web"><a href={c.website} target="_blank" rel="noopener noreferrer nofollow">Campaign website ↗</a></p>}
      {answers.length > 0 ? (
        <dl className="rp-pos">
          {answers.map(({ i, text }) => (
            <div key={i.id} className="rp-pos-row">
              <dt>{c.name} on {i.name}</dt>
              <dd>{text}{src(i.id) && <> <a href={src(i.id)} target="_blank" rel="noopener noreferrer nofollow" className="rp-pos-src">Source ↗</a></>}</dd>
            </div>
          ))}
        </dl>
      ) : <p className="rp-muted">No public positions on file yet.</p>}
    </details>
  );
}

// ── /results/IL/IL-governor — the race's own page: results, then every candidate's positions ──
function RacePage({ state, raceId, go }) {
  const [rows, setRows] = useState(undefined);
  const stateName = STATE_NAMES[state];

  useEffect(() => {
    let off = false;
    setRows(undefined);
    getJson(`${API_URL}/candidates?raceId=${encodeURIComponent(raceId)}`)
      .then(d => {
        const list = (d.candidates || []).filter(c => c.hidden !== 'true' && (c.racetype === 'measures' ? (c.shortName || c.officialTitle) : (c.name && c.name !== 'TBD')));
        if (!off) setRows(list);
      })
      .catch(() => { if (!off) setRows(null); });
    return () => { off = true; };
  }, [raceId]);

  const race = rows === undefined ? undefined : rows && rows.length ? summaryFromRows(rows) : null;
  const isMeasures = race?.t === 'measures';
  const raceType = race?.t;
  const issues = useMemo(() => (raceType && raceType !== 'measures' ? getIssues(raceType) : []), [raceType]);

  useEffect(() => {
    if (race === undefined) return;
    const title = race ? race.title : `${stateName} race`;
    const names = race?.c ? race.c.map(c => c.n) : [];
    const vs = names.length >= 2 && names.length <= 3 ? `: ${names.join(' vs. ')}` : '';
    const winner = race?.c?.find(c => c.w);
    const topics = issues.slice(0, 4).map(i => i.name.toLowerCase()).join(', ');
    setMeta({
      title: isMeasures ? `${title} 2026 — What Yes and No Mean, and Results | IndTimes Vote` : `${title} 2026${vs} — Candidates, Positions & Results | IndTimes Vote`,
      description: !race ? `2026 election in ${stateName}.`
        : isMeasures ? `Every statewide ballot measure in ${stateName} on November 3, 2026: what a Yes and a No vote mean, who put each one on the ballot, and live results.`
        : (winner ? `${winner.n} wins the ${title} race (unofficial). ` : '') + `${names.slice(0, 4).join(', ')}${names.length > 4 ? ' and others' : ''} — ${title}, November 3, 2026. Where each candidate stands${topics ? ` on ${topics}` : ''}, side by side, plus live results.`,
      path: `/results/${state}/${encodeURIComponent(raceId)}`,
      crumbs: [['Home', '/'], ['2026 Ballot', '/ballot'], [stateName, `/ballot/${state}`], [title, `/results/${state}/${encodeURIComponent(raceId)}`]],
    });
  }, [race, raceId, state, stateName, isMeasures, issues]);

  return (
    <>
      <section className="rp-hero">
        <div className="rp-hero-inner">
          <nav className="rp-crumbs" aria-label="Breadcrumb">
            <Link href="/ballot" go={go}>2026 Ballot</Link> › <Link href={`/ballot/${state}`} go={go}>{stateName}</Link>
          </nav>
          <h1 className="rp-title">{race ? `${race.title} 2026` : race === null ? 'Race not found' : 'Loading…'}</h1>
          <p className="rp-lead">{isMeasures ? 'What each measure does, and the results' : 'Candidates, where they stand, and the results'} · November 3, 2026 general election</p>
        </div>
      </section>
      <main className="rp-body">
        {race === undefined ? <div className="rp-loading"><span className="rp-spinner" /> Loading…</div>
          : race === null ? <p className="rp-muted">We couldn't find that race. <Link href={`/ballot/${state}`} go={go}>See the {stateName} ballot →</Link></p>
          : (
            <>
              <h2 className="rp-h2">Results</h2>
              <article className="rp-card rp-card--big">
                <ResultsTable race={race} />
              </article>
              {isMeasures ? (
                <section className="rp-cands">
                  <h2 className="rp-h2">The measures</h2>
                  {[...rows].sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0)).map(m => (
                    <details key={m.candidateid} className="rp-cand" open>
                      <summary><h3 className="rp-cand-name">{m.shortName || m.officialTitle}</h3></summary>
                      {m.summary && <p className="rp-cand-bio">{m.summary}</p>}
                      {m.yesMeaning && <p className="rp-cand-bio">{m.yesMeaning}</p>}
                      {m.noMeaning && <p className="rp-cand-bio">{m.noMeaning}</p>}
                      {m.whoPutOnBallot && <p className="rp-cand-role">Put on the ballot by: {m.whoPutOnBallot}</p>}
                    </details>
                  ))}
                </section>
              ) : (
                <section className="rp-cands">
                  <h2 className="rp-h2">The candidates and where they stand</h2>
                  {[...rows].sort((a, b) => a.name.localeCompare(b.name)).map(c => (
                    <CandidateProfile key={c.candidateid} c={c} issues={issues} open={rows.length <= 3} />
                  ))}
                </section>
              )}
            </>
          )}
        <p className="rp-back-row">
          <Link href={`/ballot/${state}`} go={go}>← The whole {stateName} ballot</Link>
          <a href="/" className="rp-cta">See your own ballot →</a>
        </p>
        <About />
      </main>
    </>
  );
}

function About() {
  return (
    <section className="rp-about">
      <h2 className="rp-h2">About these results</h2>
      <p>Results are unofficial. They are gathered from public sources — state and county election offices, the Associated Press and major news outlets — through Google Search, and checked through the night. A race is marked with a winner (✓) only after a major news organization or the official count has projected or declared one. Official results come from your state election office once the count is certified.</p>
      <p>See an error? Email <a href="mailto:corrections@indtimes.news">corrections@indtimes.news</a>.</p>
    </section>
  );
}

// ── /ballot ──
function BallotLanding({ go }) {
  useEffect(() => {
    setMeta({
      title: "What's on My Ballot? November 3, 2026 Ballot for Every State | IndTimes Vote",
      description: "See what's on the November 3, 2026 ballot in your state: U.S. Senate, U.S. House, Governor, statewide offices, ballot measures and State Legislature candidates. Free, non-partisan voter guide from Independent Times News.",
      path: '/ballot', crumbs: [['Home', '/'], ['2026 Ballot', '/ballot']],
    });
  }, []);
  return (
    <>
      <section className="rp-hero">
        <div className="rp-hero-inner">
          <p className="rp-eyebrow">Election Day: Tuesday, November 3, 2026</p>
          <h1 className="rp-title">What's on Your Ballot</h1>
          <p className="rp-lead">Every race and candidate on the November 3, 2026 general election ballot, state by state — free and non-partisan.</p>
          <p className="rp-lead"><a href="/" className="rp-hero-cta">Enter your address to see your own ballot and compare the candidates →</a></p>
        </div>
      </section>
      <main className="rp-body">
        <section>
          <h2 className="rp-h2">2026 ballot by state</h2>
          <ul className="rp-states">
            {Object.entries(STATE_NAMES).map(([code, nm]) => (
              <li key={code}><Link href={`/ballot/${code}`} go={go} className="rp-state">{nm} ballot</Link></li>
            ))}
          </ul>
        </section>
        <BallotAbout />
      </main>
    </>
  );
}

// ── /ballot/IL ──
function BallotState({ state, go }) {
  const [races, setRaces] = useState(null);
  const stateName = STATE_NAMES[state];
  useEffect(() => {
    setMeta({
      title: `${stateName} Ballot 2026 — Every Race and Candidate on the November 3 Ballot | IndTimes Vote`,
      description: `What's on the ${stateName} ballot on November 3, 2026: U.S. Senate, U.S. House, Governor, statewide offices, ballot measures and State Legislature candidates. Compare the candidates side by side and follow live results.`,
      path: `/ballot/${state}`, crumbs: [['Home', '/'], ['2026 Ballot', '/ballot'], [stateName, `/ballot/${state}`]],
    });
    let off = false;
    setRaces(null);
    loadStateIndex(state).then(r => { if (!off) setRaces(r); }).catch(() => { if (!off) setRaces([]); });
    return () => { off = true; };
  }, [state, stateName]);

  return (
    <>
      <section className="rp-hero">
        <div className="rp-hero-inner">
          <nav className="rp-crumbs" aria-label="Breadcrumb"><Link href="/ballot" go={go}>2026 Ballot</Link> › <span>{stateName}</span></nav>
          <h1 className="rp-title">{stateName} Ballot: November 3, 2026</h1>
          <p className="rp-lead">Every race and candidate on the {stateName} general election ballot. Which State Senate, State House and U.S. House races you vote in depends on your address.</p>
          <p className="rp-lead"><a href="/" className="rp-hero-cta">Enter your address to see your own ballot →</a> · <Link href={`/results/${state}`} go={go} className="rp-hero-cta">{stateName} results →</Link></p>
        </div>
      </section>
      <main className="rp-body">
        {races === null ? <div className="rp-loading"><span className="rp-spinner" /> Loading the {stateName} ballot…</div>
          : races.length === 0 ? <p className="rp-muted">The {stateName} ballot is on its way. <a href="/">Enter your address to see your candidates →</a></p>
          : SECTIONS.map(([label, test, open]) => {
            const list = races.filter(r => test(r.t));
            if (!list.length) return null;
            return (
              <details key={label} className="rp-section" open={open || list.length <= 6}>
                <summary><h2 className="rp-h2 rp-h2--inline">{label}</h2> <span className="rp-count">{list.length} race{list.length > 1 ? 's' : ''}</span></summary>
                <div className="rp-grid">
                  {list.map(r => (
                    <article key={r.id} className="rp-card">
                      <h3 className="rt-title">{r.title}</h3>
                      <ul className="rp-ballot-list">
                        {r.t === 'measures'
                          ? (r.m || []).map((m, i) => <li key={i}>{m.n}</li>)
                          : (r.c || []).slice().sort((a, b) => a.n.localeCompare(b.n)).map((c, i) => (
                            <li key={i}><span className="rt-dot" style={{ background: partyColor(c.p) }} /> <strong>{c.n}</strong>{c.p ? <span className="rp-ballot-party"> · {c.p}</span> : null}</li>
                          ))}
                      </ul>
                      <p className="rp-ballot-links">
                        <Link href={`/results/${state}/${encodeURIComponent(r.id)}`} go={go}>{r.t === 'measures' ? 'What they mean & results →' : 'Positions & results →'}</Link>
                      </p>
                    </article>
                  ))}
                </div>
              </details>
            );
          })}
        <p className="rp-back-row"><Link href="/ballot" go={go}>← All states</Link></p>
        <BallotAbout />
      </main>
    </>
  );
}

function BallotAbout() {
  return (
    <section className="rp-about">
      <h2 className="rp-h2">About this voter guide</h2>
      <p>IndTimes Vote — the Indy Voting Guide from Independent Times News — is a free, non-partisan guide to the November 3, 2026 general election. Enter your address to see the candidates on your own ballot and compare where they stand on the issues, side by side. For polling places, hours and voter registration, check your state or county election office.</p>
      <p>See an error? Email <a href="mailto:corrections@indtimes.news">corrections@indtimes.news</a>.</p>
    </section>
  );
}

// ── /guide — plain answers to the questions people search before and after Election Day ──
const GUIDE_FAQ = [
  ['When is Election Day 2026?', 'Election Day is Tuesday, November 3, 2026. It is a general election, often called the 2026 midterm election because it falls halfway through the president\'s term. Many states also let you vote early or by mail before November 3 — your state election office lists the dates.'],
  ["What's on the ballot on November 3, 2026?", 'Every one of the 435 seats in the U.S. House of Representatives, about a third of the U.S. Senate, governors in most states, other statewide offices such as attorney general, secretary of state and treasurer, state legislature seats, state boards and commissions, and statewide ballot measures. What you see depends on where you live — enter your address in our voter guide to see your own ballot.'],
  ['How do I see what is on my ballot?', 'Enter your street address and ZIP code on the IndTimes Vote home page. The guide shows every race on your ballot — from U.S. Senate down to your State Senate and State House districts — with the candidates side by side. You can also browse the ballot state by state.'],
  ['How can I compare the candidates?', 'Open any race in the guide to see each candidate\'s background and where they stand on the issues that office handles, with links to the sources. Positions come from the candidates\' own websites, statements and public records.'],
  ['Where do I vote, and when are the polls open?', 'Polling places and hours are set by each state and county. Your state or county election office lists your polling place, hours, and what ID you may need. Vote.gov links to every state\'s election office.'],
  ['When will the 2026 election results be known?', 'Results start coming in after polls close on the evening of November 3. Many races are projected that night; close races and states that count mail ballots later can take days. Official results are certified by each state in the weeks after the election. Follow live, unofficial results for every race on our results page.'],
  ['Is this voter guide non-partisan?', 'Yes. IndTimes Vote is a free, non-partisan voter guide from Independent Times News. Every candidate on the ballot is listed, and each one is asked about the same issues.'],
];

function GuidePage({ go }) {
  useEffect(() => {
    setMeta({
      title: '2026 Midterm Election Guide: Election Day Nov 3, Your Ballot, How to Vote & Results | IndTimes Vote',
      description: "Everything for the November 3, 2026 midterm election: what's on your ballot, how to compare the candidates, where to vote, and when results come in. Free, non-partisan voter guide from Independent Times News.",
      path: '/guide', crumbs: [['Home', '/'], ['2026 Election Guide', '/guide']],
    });
    // FAQ data for search engines
    let ld = document.getElementById('ld-faq');
    if (!ld) { ld = document.createElement('script'); ld.type = 'application/ld+json'; ld.id = 'ld-faq'; document.head.appendChild(ld); }
    ld.textContent = JSON.stringify({ '@context': 'https://schema.org', '@type': 'FAQPage',
      mainEntity: GUIDE_FAQ.map(([q, a]) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })) });
    return () => { ld.remove(); };
  }, []);
  return (
    <>
      <section className="rp-hero">
        <div className="rp-hero-inner">
          <p className="rp-eyebrow">Election Day: Tuesday, November 3, 2026</p>
          <h1 className="rp-title">2026 Election Guide</h1>
          <p className="rp-lead">Your ballot, the candidates, how to vote, and the results — for the November 3, 2026 midterm election.</p>
          <p className="rp-lead"><a href="/" className="rp-hero-cta">See what's on your ballot →</a> · <Link href="/ballot" go={go} className="rp-hero-cta">Ballot by state →</Link> · <Link href="/results" go={go} className="rp-hero-cta">Live results →</Link></p>
        </div>
      </section>
      <main className="rp-body rp-guide">
        {GUIDE_FAQ.map(([q, a]) => (
          <section key={q} className="rp-faq">
            <h2 className="rp-h2">{q}</h2>
            <p>{a}</p>
          </section>
        ))}
        <section className="rp-faq">
          <h2 className="rp-h2">Ballots by state</h2>
          <ul className="rp-states">
            {Object.entries(STATE_NAMES).map(([code, nm]) => (
              <li key={code}><Link href={`/ballot/${code}`} go={go} className="rp-state">{nm} ballot</Link></li>
            ))}
          </ul>
        </section>
        <BallotAbout />
      </main>
    </>
  );
}

function ResultsPage() {
  const [route, setRoute] = useState(parsePath);
  const go = useCallback(href => {
    window.history.pushState({}, '', href);
    setRoute(parsePath());
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    const onPop = () => setRoute(parsePath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  // Race titles inside the tables are plain links — keep them inside the page instead of reloading it
  const onClick = e => {
    const a = e.target.closest && e.target.closest('a[data-nav]');
    if (a && !(e.metaKey || e.ctrlKey || e.shiftKey)) { e.preventDefault(); go(a.getAttribute('href')); }
  };

  return (
    <div className="rp" onClick={onClick}>
      <div className="rp-topbar">
        <a href="/" className="rp-topbar-link">← Voter Guide</a>
        <a href="/ballot" className={`rp-topbar-tab${route.section === 'ballot' ? ' rp-topbar-tab--on' : ''}`} onClick={e => { e.preventDefault(); go('/ballot'); }}>Ballot</a>
        <a href="/results" className={`rp-topbar-tab${route.section === 'results' ? ' rp-topbar-tab--on' : ''}`} onClick={e => { e.preventDefault(); go('/results'); }}>Results</a>
        <a href="/guide" className={`rp-topbar-tab${route.section === 'guide' ? ' rp-topbar-tab--on' : ''}`} onClick={e => { e.preventDefault(); go('/guide'); }}>Guide</a>
      </div>
      {route.section === 'guide' ? <GuidePage go={go} />
        : route.section === 'ballot' ? (route.state ? <BallotState key={route.state} state={route.state} go={go} /> : <BallotLanding go={go} />)
        : route.raceId ? <RacePage key={route.raceId} state={route.state} raceId={route.raceId} go={go} />
        : route.state ? <StatePage key={route.state} state={route.state} go={go} initialFilter={route.q} />
        : <Landing go={go} initialQuery={route.q} />}
    </div>
  );
}

export default ResultsPage;
